// components/SubtleEnergyBackground.tsx
import React, { useRef, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { getProjectPanOffset, getProjectZoomLevel } from '#lib/project-runtime-layer-ui-state';
import { VIEWPORT_TRANSFORM_CONTAINER_SELECTOR } from '#lib/studio-viewport';
import { createStarfieldTiles, runStarfield } from '@/components/viewport/starfield';

const CANVAS_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  pointerEvents: 'none',
};

interface SubtleEnergyBackgroundProps {
  projectId: string;
}

// Sized by the caller's ResizeObserver right after it compensates the pan, so the stars paint in the frame the host
// moved; a size routed through React state lands a frame late and the canvas overflows or shakes.
export interface SubtleEnergyBackgroundHandle {
  resize: (width: number, height: number) => void;
}

const SubtleEnergyBackground = forwardRef<SubtleEnergyBackgroundHandle, SubtleEnergyBackgroundProps>(({
  projectId,
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const starfieldRef = useRef<ReturnType<typeof runStarfield> | null>(null);
  useImperativeHandle(ref, () => ({
    resize: (width, height) => starfieldRef.current?.resize(width, height),
  }), []);
  const { kernel } = useKernel();
  const viewportElementRef = useRef<HTMLDivElement | null>(null);
  const layerTilesRef = useRef<HTMLCanvasElement[]>([]);

  useLayoutEffect(() => {
    layerTilesRef.current = createStarfieldTiles();
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resolveViewportElement = () => {
      const canvasContainer = kernel.current?.canvasContainerRef.current;
      if (!canvasContainer) {
        viewportElementRef.current = null;
        return null;
      }

      const cached = viewportElementRef.current;
      if (cached && canvasContainer.contains(cached)) {
        return cached;
      }

      const found = canvasContainer.querySelector<HTMLDivElement>(VIEWPORT_TRANSFORM_CONTAINER_SELECTOR);
      viewportElementRef.current = found;
      return found;
    };

    const readViewportState = () => {
      const fallbackZoom = projectId && kernel.current ? getProjectZoomLevel(kernel.current, projectId) : 1;
      const fallbackPan = kernel.current ? getProjectPanOffset(kernel.current, projectId) : { x: 0, y: 0 };
      const viewportElement = resolveViewportElement();
      if (!viewportElement) {
        return { panOffset: fallbackPan, zoomLevel: fallbackZoom };
      }

      const transformRaw = viewportElement.style.transform.trim();
      const transformMatch = /translate\(([-+\d.eE]+)px,\s*([-+\d.eE]+)px\)\s*scale\(([-+\d.eE]+)\)/.exec(transformRaw);
      if (transformMatch) {
        const parsedPanX = Number.parseFloat(transformMatch[1] ?? '');
        const parsedPanY = Number.parseFloat(transformMatch[2] ?? '');
        const parsedZoom = Number.parseFloat(transformMatch[3] ?? '');

        return {
          zoomLevel: Number.isFinite(parsedZoom) ? parsedZoom : fallbackZoom,
          panOffset: {
            x: Number.isFinite(parsedPanX) ? parsedPanX : fallbackPan.x,
            y: Number.isFinite(parsedPanY) ? parsedPanY : fallbackPan.y,
          },
        };
      }

      return { panOffset: fallbackPan, zoomLevel: fallbackZoom };
    };

    const canvasContainer = kernel.current?.canvasContainerRef.current;
    const starfield = runStarfield({
      canvas,
      tiles: () => layerTilesRef.current,
      width: canvasContainer?.clientWidth ?? 0,
      height: canvasContainer?.clientHeight ?? 0,
      readViewportState,
    });
    starfieldRef.current = starfield;
    return () => {
      starfield();
      starfieldRef.current = null;
    };
  }, [kernel, projectId]);

  return (
    <canvas
      ref={canvasRef}
      className="subtle-energy-background"
      style={CANVAS_STYLE}
    />
  );
});

SubtleEnergyBackground.displayName = 'SubtleEnergyBackground';
export default React.memo(SubtleEnergyBackground);
